import { useState } from 'react';
import apiClient from '../services/apiClientService';
import { Routes } from '../routes/RouteConstants';
import useErrorHandler, { isBookingGone, isBookingStateChanged } from '../hooks/useErrorHandler';
import { useTenant } from '../contexts/TenantContext';

/**
 * The tenant's admin deletes a booking, or lifts a blocked period, from the dashboard:
 * DELETE …/bookings/{id}. No deadline and no mail, unlike the guest's useCancelBooking.
 *
 * What a delete came to. GONE: the guest cancelled it while the card was open (DEC-0023) — the
 * snackbar has said so, and the board should drop it. CHANGED: the booking closed while the card
 * was open (DEC-0026) — rejected, or its time passed — and the board should reload it.
 */
export const DeleteOutcome = Object.freeze({
  DELETED: 'DELETED',
  GONE: 'GONE',
  CHANGED: 'CHANGED',
  FAILED: 'FAILED',
});

const useDeleteBooking = () => {
  const { tenantId } = useTenant();
  const [loading, setLoading] = useState(false);
  const { handleError } = useErrorHandler();

  const deleteBooking = async (bookingId) => {
    setLoading(true);

    try {
      const url = `/v1/tenants/${tenantId}/bookings/${bookingId}`;
      await apiClient.delete(url);
      return DeleteOutcome.DELETED;
    } catch (error) {
      handleError(error, Routes.DASHBOARD);
      if (isBookingGone(error)) return DeleteOutcome.GONE;
      if (isBookingStateChanged(error)) return DeleteOutcome.CHANGED;
      return DeleteOutcome.FAILED;
    } finally {
      setLoading(false);
    }
  };

  return { deleteBooking, loading };
};

export default useDeleteBooking;
